import {
  generateId,
  getDatabase,
  updateDatabase
} from '../data/localDataStore';

const CLEANUP_THRESHOLD_DAYS = 20;

const bytesToMb = (value) => Number((value / (1024 * 1024)).toFixed(2));

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();

  reader.onloadend = () => {
    if (typeof reader.result === 'string') {
      resolve(reader.result);
      return;
    }

    reject(new Error('Impossibile leggere il file generato'));
  };

  reader.onerror = () => reject(new Error('Impossibile convertire il file generato'));
  reader.readAsDataURL(blob);
});

class LocalFileService {
  async uploadOrderFiles(pdfBlob, txtContent, baseFilename) {
    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const pdfId = generateId('file');
      const txtId = generateId('file');
      const pdfFilename = `${baseFilename}_${timestamp}.pdf`;
      const txtFilename = `${baseFilename}_${timestamp}.txt`;
      const txtBlob = new Blob([txtContent], { type: 'text/plain;charset=utf-8' });
      const pdfDataUrl = await blobToDataUrl(pdfBlob);
      const txtDataUrl = await blobToDataUrl(txtBlob);

      const createdAt = new Date().toISOString();

      updateDatabase((draft) => {
        draft.generatedFiles.push(
          {
            id: pdfId,
            type: 'pdf',
            name: pdfFilename,
            size: pdfBlob.size || 0,
            created_at: createdAt,
            source: 'order',
            content: pdfDataUrl
          },
          {
            id: txtId,
            type: 'txt',
            name: txtFilename,
            size: txtBlob.size || 0,
            created_at: createdAt,
            source: 'order',
            content: txtDataUrl
          }
        );
      });

      return {
        success: true,
        pdf: {
          id: pdfId,
          filename: pdfFilename
        },
        txt: {
          id: txtId,
          filename: txtFilename
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Errore nel salvataggio dei file locali'
      };
    }
  }

  async getCleanupPreview() {
    try {
      const database = getDatabase();
      const thresholdDate = Date.now() - CLEANUP_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
      const filesToDelete = database.generatedFiles.filter(
        (file) => new Date(file.created_at).getTime() < thresholdDate
      );
      const totalBytes = filesToDelete.reduce((sum, file) => sum + (file.size || 0), 0);

      return {
        success: true,
        preview: {
          filesToDelete: filesToDelete.length,
          totalSizeToFreeMB: bytesToMb(totalBytes)
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Errore nell\'anteprima pulizia archivio locale'
      };
    }
  }

  async executeAutoCleanup() {
    try {
      let deletedFiles = [];

      updateDatabase((draft) => {
        const thresholdDate = Date.now() - CLEANUP_THRESHOLD_DAYS * 24 * 60 * 60 * 1000;
        deletedFiles = draft.generatedFiles.filter(
          (file) => new Date(file.created_at).getTime() < thresholdDate
        );

        draft.generatedFiles = draft.generatedFiles.filter(
          (file) => new Date(file.created_at).getTime() >= thresholdDate
        );

        draft.cleanupHistory.unshift({
          id: generateId('cleanup'),
          created_at: new Date().toISOString(),
          deleted_count: deletedFiles.length,
          total_size_freed_mb: bytesToMb(
            deletedFiles.reduce((sum, file) => sum + (file.size || 0), 0)
          ),
          message: deletedFiles.length
            ? 'Metadati file locali puliti con successo'
            : 'Nessun file locale da pulire'
        });
      });

      const totalSizeFreedMB = bytesToMb(
        deletedFiles.reduce((sum, file) => sum + (file.size || 0), 0)
      );

      return {
        success: true,
        cleanup: {
          deletedCount: deletedFiles.length,
          totalSizeFreedMB,
          message: deletedFiles.length
            ? 'Cleanup archivio locale completato'
            : 'Nessun file locale da rimuovere'
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Errore durante il cleanup archivio locale'
      };
    }
  }

  async getEnhancedStorageStats() {
    try {
      const database = getDatabase();
      const totalBytes = database.generatedFiles.reduce((sum, file) => sum + (file.size || 0), 0);
      const previewResult = await this.getCleanupPreview();
      const filesOlderThan20Days = previewResult?.preview?.filesToDelete || 0;
      const cleanupSavingsMB = previewResult?.preview?.totalSizeToFreeMB || 0;

      let storageHealth = 'Excellent';
      if (database.generatedFiles.length > 50 || bytesToMb(totalBytes) > 100) {
        storageHealth = 'Good';
      }
      if (database.generatedFiles.length > 150 || bytesToMb(totalBytes) > 300) {
        storageHealth = 'Attention';
      }

      return {
        success: true,
        stats: {
          totalFiles: database.generatedFiles.length,
          totalSizeMB: bytesToMb(totalBytes),
          filesOlderThan20Days,
          cleanupSavingsMB,
          storageHealth
        }
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Errore nel recupero statistiche archivio locale'
      };
    }
  }

  async getCleanupHistory(limit = 5) {
    try {
      const database = getDatabase();
      return {
        success: true,
        history: database.cleanupHistory.slice(0, limit)
      };
    } catch (error) {
      return {
        success: false,
        error: error?.message || 'Errore nel recupero storico cleanup'
      };
    }
  }
}

export const storageService = new LocalFileService();
